import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterRenderEffect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import Hls from 'hls.js';
import { MbaIcon } from '../../../assignment/atoms/mba-icon/mba-icon';
import { formatDuration } from '../../approved-content.helpers';

/**
 * 9:16 preview player for the Approved Content tab: poster, caption, a centre play/pause
 * button and a progress track. Plays HLS through hls.js (native HLS on Safari) or an MP4 URL.
 * Changing `src` stops playback.
 */
@Component({
  selector: 'mbt-approved-player',
  imports: [MbaIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  // The host's box (sized by the parent) is a size container; the player takes the largest
  // size at the video's aspect ratio that fits inside it, so it never overflows either way.
  template: `
    <div class="grid size-full items-start justify-items-center [container-type:size]">
    <div
      class="relative grid place-items-center overflow-hidden rounded-[14px] bg-(--shout-deep-purple) text-white"
      [style.aspect-ratio]="aspectRatio()" [style.width]="'min(100cqw, 100cqh * ' + aspectRatio() + ')'">
      <video #video class="absolute inset-0 size-full object-cover" [poster]="poster() ?? ''" playsinline preload="metadata"
        (timeupdate)="onTime()" (loadedmetadata)="onTime()" (play)="playing.set(true)" (pause)="playing.set(false)"
        (ended)="playing.set(false)" (click)="toggle()"></video>
      <span class="pointer-events-none absolute inset-x-3 top-3 flex justify-between text-[11px] font-bold drop-shadow">
        <span>{{ caption() }}</span>
        <span>{{ formatLabel() }}</span>
      </span>
      <button type="button" (click)="toggle()" [attr.aria-label]="playing() ? 'Pause' : 'Play'"
        [class]="'relative z-[1] grid size-[58px] cursor-pointer place-items-center rounded-full bg-white/18 backdrop-blur-sm transition-[background-color,transform,opacity] duration-[140ms] ease-[cubic-bezier(0.2,0,0,1)] hover:bg-white/28 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-white/60 ' + (playing() ? 'opacity-0 hover:opacity-100 focus-visible:opacity-100' : '')"
        [disabled]="!src()">
        <mba-icon [name]="playing() ? 'pause' : 'play'" size="size-6" />
      </button>
      <span class="pointer-events-none absolute inset-x-3 bottom-3 flex items-center gap-2 text-[11px] font-bold tabular-nums drop-shadow">
        <span>{{ elapsedLabel() }}</span>
        <span class="h-1 flex-1 overflow-hidden rounded-full bg-white/30"><i class="block h-full bg-white" [style.width.%]="progress()"></i></span>
        <span>{{ durationLabel() }}</span>
      </span>
    </div>
    </div>
  `,
})
export class ApprovedPlayer {
  readonly src = input<string | null>(null);
  readonly isHls = input(false);
  readonly poster = input<string | null>(null);
  readonly caption = input('');
  readonly formatLabel = input('');
  /** Fallback until the video's own metadata loads. */
  readonly durationSec = input(0);
  /** Width ÷ height of the video: 9/16 portrait, 16/9 landscape. */
  readonly aspectRatio = input(9 / 16);

  protected readonly playing = signal(false);
  protected readonly progress = signal(0);
  protected readonly elapsedLabel = signal('0:00');
  protected readonly durationLabel = signal('0:00');

  private readonly videoRef = viewChild.required<ElementRef<HTMLVideoElement>>('video');
  private hls: Hls | null = null;

  constructor() {
    // After render, so the <video> element exists before a source is attached.
    afterRenderEffect(() => {
      const src = this.src();
      const isHls = this.isHls();
      const fallback = this.durationSec();
      untracked(() => this.load(src, isHls, fallback));
    });
    inject(DestroyRef).onDestroy(() => this.teardown());
  }

  /** Pauses playback (e.g. when the preview moves to another video). */
  pause(): void {
    this.videoRef().nativeElement.pause();
  }

  protected toggle(): void {
    const el = this.videoRef().nativeElement;
    if (!this.src()) return;
    if (el.paused) void el.play().catch(() => this.playing.set(false));
    else el.pause();
  }

  protected onTime(): void {
    const el = this.videoRef().nativeElement;
    const duration = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : this.durationSec();
    this.elapsedLabel.set(formatDuration(el.currentTime));
    this.durationLabel.set(formatDuration(duration));
    this.progress.set(duration > 0 ? Math.min(100, (el.currentTime / duration) * 100) : 0);
  }

  private load(src: string | null, isHls: boolean, fallbackDuration: number): void {
    const el = this.videoRef().nativeElement;
    this.teardown();
    this.playing.set(false);
    this.progress.set(0);
    this.elapsedLabel.set('0:00');
    this.durationLabel.set(formatDuration(fallbackDuration));
    if (!src) {
      el.removeAttribute('src');
      el.load();
      return;
    }
    if (isHls && Hls.isSupported()) {
      this.hls = new Hls();
      this.hls.loadSource(src);
      this.hls.attachMedia(el);
    } else {
      el.src = src;
    }
  }

  private teardown(): void {
    const el = this.videoRef().nativeElement;
    el.pause();
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
  }
}
